// src/views/ConsultantDashboard.jsx
// Mini-dashboard du CONSULTANT :
//   1. choisir un créateur (parmi ceux attribués par l'admin)
//   2. voir / rechercher / filtrer ses événements
//   3. Ajouter (AddEvent2) / Modifier (UpdateEvent2) au nom de ce créateur
// Un signal d'activité est envoyé toutes les 60 s (temps de travail réel).

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Plus, Pencil, Search, Calendar, MapPin, Clock, CheckCircle2, LogOut, Users, RefreshCw } from "lucide-react";
import {
    getMesCreateurs,
    envoyerHeartbeat,
    getMonActivite,
    getEvenementsCreateurActif,
    formatDuree,
} from "../services/ConsultantService";
import { getCreateurActif, setCreateurActif, estAdminEspace, peutAgirPour, fermerEspace } from "../utils/agirPour";

const VIOLET = "#481699";

// ── Heartbeat : seulement si l'onglet est visible ET qu'il y a eu
//    une activité (souris/clavier) dans les 2 dernières minutes ──
function useHeartbeat(createurId) {
    useEffect(() => {
        let derniereActivite = Date.now();
        const marquer = () => { derniereActivite = Date.now(); };
        const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        evts.forEach((e) => window.addEventListener(e, marquer, { passive: true }));

        const envoyer = () => {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - derniereActivite > 120000) return; // inactif → pas compté
            envoyerHeartbeat(createurId).catch(() => { });
        };

        envoyer();
        const timer = setInterval(envoyer, 60000);
        return () => {
            clearInterval(timer);
            evts.forEach((e) => window.removeEventListener(e, marquer));
        };
    }, [createurId]);
}

// Pagination : ‹ 1 … 4 5 6 … 12 ›
function Pagination({ pagination, onPage }) {
    const { current_page: p, last_page: n } = pagination;
    const pages = [];
    for (let i = 1; i <= n; i++) {
        if (i === 1 || i === n || Math.abs(i - p) <= 1) pages.push(i);
        else if (pages[pages.length - 1] !== "…") pages.push("…");
    }
    const btn = "min-w-[36px] h-9 px-2 rounded-lg text-sm font-semibold border";
    return (
        <div className="flex justify-center items-center gap-1.5 mt-6 flex-wrap">
            <button disabled={p === 1} onClick={() => onPage(p - 1)}
                className={`${btn} bg-white border-gray-200 text-gray-600 disabled:opacity-40`}>‹</button>
            {pages.map((x, i) => x === "…" ? (
                <span key={`e${i}`} className="px-1 text-gray-400">…</span>
            ) : (
                <button key={x} onClick={() => onPage(x)}
                    className={`${btn} ${x === p ? "text-white border-transparent" : "bg-white border-gray-200 text-gray-600 hover:border-purple-400"}`}
                    style={x === p ? { backgroundColor: VIOLET } : {}}>{x}</button>
            ))}
            <button disabled={p === n} onClick={() => onPage(p + 1)}
                className={`${btn} bg-white border-gray-200 text-gray-600 disabled:opacity-40`}>›</button>
        </div>
    );
}

function imageEvent(ev) {
    const img = ev.imgEven || ev.image;
    if (!img || img === "empty") return null;
    return img.split("#")[0];
}

export default function ConsultantDashboard() {
    const navigate = useNavigate();

    const [createurs, setCreateurs] = useState([]);
    const [actif, setActif] = useState(getCreateurActif());
    const [loadingCreateurs, setLoadingCreateurs] = useState(true);

    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [erreur, setErreur] = useState(null);

    const [recherche, setRecherche] = useState("");
    const [filtre, setFiltre] = useState("tous"); // tous | avenir | passes

    const [activite, setActivite] = useState(null);

    // L'admin voit TOUS les créateurs → recherche
    const estAdmin = estAdminEspace();
    const [rechercheCreateur, setRechercheCreateur] = useState("");
    useEffect(() => {
        if (!estAdmin) return;
        const t = setTimeout(() => getMesCreateurs(rechercheCreateur).then(setCreateurs).catch(() => { }), 300);
        return () => clearTimeout(t);
    }, [estAdmin, rechercheCreateur]);

    useHeartbeat(actif?.id);

    // ── Créateurs attribués ──
    useEffect(() => {
        getMesCreateurs()
            .then((liste) => {
                setCreateurs(liste);
                // Créateur actif retiré par l'admin → on le désélectionne
                const courant = getCreateurActif();
                if (courant && !liste.some((c) => c.id === courant.id)) {
                    setCreateurActif(null);
                    setActif(null);
                }
                // Un seul créateur → sélection automatique
                if (!courant && liste.length === 1) {
                    setCreateurActif(liste[0]);
                    setActif(getCreateurActif());
                }
            })
            .catch((e) => setErreur(e.message))
            .finally(() => setLoadingCreateurs(false));

        getMonActivite().then(setActivite).catch(() => { });
    }, []);

    // ── Événements du créateur actif ──
    // Recherche envoyée au serveur 400 ms après la frappe
    const [rechercheServeur, setRechercheServeur] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setRechercheServeur(recherche.trim()), 400);
        return () => clearTimeout(t);
    }, [recherche]);

    const chargerEvents = useCallback(async (page = 1) => {
        if (!actif) return;
        setLoadingEvents(true);
        setErreur(null);
        try {
            const d = await getEvenementsCreateurActif(page, rechercheServeur, filtre);
            setEvents(d.events);
            setPagination(d.pagination);
        } catch (e) {
            setErreur(e.message);
            setEvents([]);
        } finally {
            setLoadingEvents(false);
        }
    }, [actif, rechercheServeur, filtre]);

    useEffect(() => { chargerEvents(1); }, [chargerEvents]);

    const choisir = (c) => {
        setCreateurActif(c);
        setActif(getCreateurActif());
        setRecherche("");
    };

    // Recherche + filtres faits côté serveur
    const eventsFiltres = events;

    const deconnexion = () => {
        const espace = fermerEspace(); // retire token_consultant / espace / créateur actif
        navigate(espace === "admin" ? "/admin/consultants" : "/consultant/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* ══════════ EN-TÊTE ══════════ */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
                    <div className="font-extrabold text-lg" style={{ color: VIOLET }}>Thrundrz</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 font-semibold" style={{ color: VIOLET }}>
                        Espace consultant
                    </span>
                    <div className="ml-auto flex items-center gap-4">
                        {activite && (
                            <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><Clock size={14} /> {formatDuree(activite.temps_secondes)} aujourd'hui</span>
                                <span>{activite.ajouts} ajout{activite.ajouts > 1 ? "s" : ""}</span>
                                <span>{activite.modifications} modif{activite.modifications > 1 ? "s" : ""}</span>
                            </div>
                        )}
                        <button onClick={deconnexion} className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600">
                            <LogOut size={16} /> <span className="hidden sm:inline">{estAdmin ? "Retour admin" : "Déconnexion"}</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6">
                {/* ══════════ CHOIX DU CRÉATEUR ══════════ */}
                <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Users size={18} style={{ color: VIOLET }} />
                        <h2 className="font-bold text-gray-800">Je travaille pour</h2>
                        {estAdmin && <span className="text-xs text-gray-400">(admin : tous les créateurs)</span>}
                    </div>

                    {estAdmin && (
                        <div className="relative mb-3">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={rechercheCreateur}
                                onChange={(e) => setRechercheCreateur(e.target.value)}
                                placeholder="Rechercher un créateur (nom ou e-mail)…"
                                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-base sm:text-sm outline-none focus:border-purple-500"
                            />
                        </div>
                    )}

                    {loadingCreateurs ? (
                        <p className="text-sm text-gray-400">Chargement…</p>
                    ) : createurs.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            Aucun créateur ne vous est attribué. Contactez l'administrateur Thrundrz.
                        </p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {createurs.map((c) => {
                                const choisi = actif?.id === c.id;
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => choisir(c)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition ${choisi
                                            ? "text-white border-transparent shadow"
                                            : "bg-white text-gray-700 border-gray-200 hover:border-purple-400"}`}
                                        style={choisi ? { backgroundColor: VIOLET } : {}}
                                    >
                                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${choisi ? "bg-white/20" : "bg-purple-100"}`}
                                            style={choisi ? {} : { color: VIOLET }}>
                                            {(c.nomComplet || "?").charAt(0).toUpperCase()}
                                        </span>
                                        <span className="font-semibold">{c.nomComplet}</span>
                                        <span className={`text-xs ${choisi ? "text-white/70" : "text-gray-400"}`}>{c.nb_evenements} év.</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* ══════════ ÉVÉNEMENTS DU CRÉATEUR ══════════ */}
                {actif && (
                    <section>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                            <h2 className="text-xl font-bold text-gray-800">
                                Événements de <span style={{ color: VIOLET }}>{actif.nom}</span>
                                <span className="text-sm font-normal text-gray-400 ml-2">({pagination.total})</span>
                            </h2>
                            <div className="sm:ml-auto flex gap-2">
                                <button onClick={() => chargerEvents(pagination.current_page)}
                                    className="p-2.5 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-gray-800" title="Actualiser">
                                    <RefreshCw size={16} />
                                </button>
                                <button
                                    onClick={() => navigate("/consultant/ajouter")}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm shadow"
                                    style={{ backgroundColor: VIOLET }}
                                >
                                    <Plus size={16} /> Ajouter un événement
                                </button>
                            </div>
                        </div>

                        {/* Filtres */}
                        <div className="flex flex-col sm:flex-row gap-2 mb-4">
                            <div className="relative flex-1">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={recherche}
                                    onChange={(e) => setRecherche(e.target.value)}
                                    placeholder="Rechercher un événement ou une ville…"
                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 bg-white text-base sm:text-sm outline-none focus:border-purple-500"
                                />
                            </div>
                            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
                                {[["tous", "Tous"], ["avenir", "À venir"], ["passes", "Passés"]].map(([v, l]) => (
                                    <button key={v} onClick={() => setFiltre(v)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filtre === v ? "text-white" : "text-gray-500"}`}
                                        style={filtre === v ? { backgroundColor: VIOLET } : {}}>
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {pagination.total > 0 && (
                            <p className="text-xs text-gray-400 mb-2">
                                {(pagination.current_page - 1) * 10 + 1}–{Math.min(pagination.current_page * 10, pagination.total)} sur {pagination.total} événement{pagination.total > 1 ? "s" : ""}
                            </p>
                        )}

                        {erreur && <div className="bg-red-50 text-red-600 rounded-xl p-3 mb-4 text-sm border border-red-200">{erreur}</div>}

                        {loadingEvents ? (
                            <p className="text-center text-gray-400 py-16">Chargement…</p>
                        ) : eventsFiltres.length === 0 ? (
                            <p className="text-center text-gray-400 py-16 bg-white rounded-2xl border border-gray-200">Aucun événement.</p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {eventsFiltres.map((ev) => {
                                    const img = imageEvent(ev);
                                    const passe = ev.dateEven && new Date(ev.dateEven) < new Date();
                                    return (
                                        <div key={ev.id} className="bg-white rounded-2xl border border-gray-200 p-3 flex items-center gap-3 hover:shadow-sm transition">
                                            {img ? (
                                                <img src={img} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                                            ) : (
                                                <div className="w-16 h-16 rounded-xl bg-purple-50 shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-bold text-gray-800 truncate">{ev.nomEven}</p>
                                                    {passe && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">Passé</span>}
                                                    {ev.isActive === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 shrink-0">Inactif</span>}
                                                </div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mt-1">
                                                    {ev.dateEven && <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(ev.dateEven).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</span>}
                                                    {ev.villeEven && <span className="flex items-center gap-1"><MapPin size={12} /> {ev.villeEven}</span>}
                                                    {ev.billetterie_type === "thrundrz" && <span className="flex items-center gap-1 text-green-600"><CheckCircle2 size={12} /> Billetterie</span>}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/consultant/modifier/${ev.id}`)}
                                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-purple-400 hover:text-purple-700 shrink-0"
                                            >
                                                <Pencil size={14} /> <span className="hidden sm:inline">Modifier</span>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {pagination.last_page > 1 && (
                            <Pagination pagination={pagination} onPage={chargerEvents} />
                        )}
                    </section>
                )}
            </main>
        </div>
    );
}

// ══════════════════════════════════════════════════════════
//  GARDE : protège toutes les pages /consultant
//  (pas de token consultant / admin → /consultant/login)
// ══════════════════════════════════════════════════════════
export function ConsultantRoute({ children }) {
    if (!peutAgirPour()) return <Navigate to="/consultant/login" replace />;
    return children;
}

// ══════════════════════════════════════════════════════════
//  CADRE pour AddEvent2 / UpdateEvent2 côté consultant :
//  bandeau "Vous agissez pour X" + retour + heartbeat
// ══════════════════════════════════════════════════════════
export function ConsultantCadre({ children }) {
    const navigate = useNavigate();
    const actif = getCreateurActif();
    useHeartbeat(actif?.id);

    useEffect(() => {
        if (!actif) navigate("/consultant", { replace: true });
    }, [actif, navigate]);

    if (!actif) return null;

    return (
        <div className="min-h-screen bg-white">
            <div className="sticky top-0 z-30 text-white px-4 py-2.5 flex items-center gap-3 text-sm" style={{ backgroundColor: VIOLET }}>
                <button onClick={() => navigate("/consultant")} className="px-3 py-1 rounded-lg bg-white/15 hover:bg-white/25">
                    ← Retour
                </button>
                <span>
                    Vous agissez pour <strong>{actif.nom}</strong> : l'événement sera publié sur son compte.
                </span>
            </div>
            {children}
        </div>
    );
}